import { NIAT_MAX_CHARS } from '@iqrapath/shared'
import { useQueryClient } from '@tanstack/react-query'
import { Check, Plus, Trash2, Undo2 } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { EmptyState } from '../../components/EmptyState.tsx'
import { SkRows } from '../../components/Skeleton.tsx'
import { ApiError } from '../../lib/api.ts'
import { sparkBurst } from '../../lib/motion.ts'
import { useToast } from '../../lib/toast.ts'
import { useGarden } from './LevelUp.tsx'
import { Back } from './parts.tsx'
import { addNiat, deleteNiat, refreshPerjalanan, setNiatDone, useNiat, type Niat } from './perjalanan.ts'

const SOURCE_LABEL: Record<Niat['source'], string> = { pegangan: 'dari Pegangan', ayat: 'dari ayat harian', tantangan: 'dari Tantangan', mutiara: 'dari Mutiara', sendiri: '' }
const msg = (e: unknown) => (e instanceof ApiError && e.status !== 0 ? e.message : 'Belum tersimpan. Cek koneksi, lalu coba lagi.')

function NiatRow({ n, onDone, onUndo, onDelete }: { n: Niat; onDone?: (el: HTMLElement) => void; onUndo?: () => void; onDelete: () => void }) {
  const src = SOURCE_LABEL[n.source]
  return (
    <li className={`nt-row${n.doneAt ? ' done' : ''}`}>
      {onDone ? (
        <button type="button" role="checkbox" aria-checked={false} className="hb-check" onClick={(e) => onDone(e.currentTarget)}>
          <span className="hb-box" aria-hidden="true" />
          <span className="hb-label">{n.body}</span>
        </button>
      ) : (
        <span className="hb-check is-done"><span className="hb-box" aria-hidden="true"><Check size={16} strokeWidth={3} /></span><span className="hb-label">{n.body}</span></span>
      )}
      {src && (n.link ? <Link className="nt-src" to={n.link}>{src}</Link> : <span className="nt-src">{src}</span>)}
      <span className="nt-act">
        {onUndo && <button type="button" className="icon-btn plain" aria-label={`Batalkan selesai: ${n.body}`} onClick={onUndo}><Undo2 size={18} aria-hidden="true" /></button>}
        <button type="button" className="icon-btn plain" aria-label={`Hapus niat: ${n.body}`} onClick={onDelete}><Trash2 size={18} aria-hidden="true" /></button>
      </span>
    </li>
  )
}

/** /perjalanan/niat — Niat & Amal (docs/PRD.md §6.12): open intentions, ticked ones of the last 7 days, and their source. */
export function NiatPage() {
  const { data, isPending, isError, refetch } = useNiat()
  const qc = useQueryClient()
  const [toast, show] = useToast()
  const { after, overlay } = useGarden()
  const [text, setText] = useState('')

  const run = async (fn: () => Promise<unknown>) => {
    try {
      await fn()
    } catch (e) {
      show(msg(e))
    }
  }
  const add = (e: FormEvent) => {
    e.preventDefault()
    void run(async () => {
      await addNiat(text.trim())
      setText('')
      await refreshPerjalanan(qc)
    })
  }

  return (
    <div className="page pj">
      <Back />
      <h1>Niat &amp; Amal</h1>
      <p className="muted" style={{ marginTop: -6 }}>Niat kecil yang ingin kamu kerjakan. Centang saat sudah, semoga jadi amal.</p>

      <form className="card nt-add nt-add-big" onSubmit={add}>
        <label className="sr" htmlFor="nt-in">Niat baru</label>
        <input id="nt-in" className="input" value={text} maxLength={NIAT_MAX_CHARS} onChange={(e) => setText(e.target.value)} placeholder="Tulis satu niat kecil" />
        <button type="submit" className="btn btn-primary" disabled={!text.trim()}><Plus size={18} aria-hidden="true" /> Tambah</button>
      </form>

      {isPending && <SkRows label="Memuat niat" rows={3} />}
      {isError && (
        <EmptyState obj="kendi" title="Belum bisa dimuat" action={<button className="btn btn-primary" onClick={() => refetch()}>Coba lagi</button>}>
          Koneksi lagi bermasalah. Cek internet kamu, lalu coba lagi.
        </EmptyState>
      )}
      {data && data.open.length === 0 && data.done.length === 0 && (
        <EmptyState obj="kendi" title="Belum ada niat">Tulis di atas, atau tekan “Jadikan niat” pada langkah kecil di kartu Pegangan dan ayat harian.</EmptyState>
      )}
      {data && data.open.length > 0 && (
        <>
          <h2 className="section-t">Belum dikerjakan</h2>
          <ul className="card nt-full" aria-label="Niat yang belum dikerjakan">
            {data.open.map((n) => (
              <NiatRow
                key={n.id}
                n={n}
                onDone={(el) => { sparkBurst(el, 12); void run(async () => after((await setNiatDone(n.id, true)).garden)) }}
                onDelete={() => void run(async () => { await deleteNiat(n.id); await refreshPerjalanan(qc) })}
              />
            ))}
          </ul>
        </>
      )}
      {data && data.done.length > 0 && (
        <>
          <h2 className="section-t">Sudah dikerjakan · 7 hari terakhir</h2>
          <ul className="card nt-full" aria-label="Niat yang sudah dikerjakan">
            {data.done.map((n) => (
              <NiatRow
                key={n.id}
                n={n}
                onUndo={() => void run(async () => after((await setNiatDone(n.id, false)).garden))}
                onDelete={() => void run(async () => { await deleteNiat(n.id); await refreshPerjalanan(qc) })}
              />
            ))}
          </ul>
        </>
      )}
      <div className="toast-slot" role="status">{toast && <p className="toast">{toast}</p>}</div>
      {overlay}
    </div>
  )
}
